import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ErrorLike } from '@apollo/client';
import { useQuery, skipToken } from "@apollo/client/react";
import useAuth from '../hooks/useAuth';
import {
    MessagingConversation,
    MessagingConversationConnection,
    MessagingConversationFiltersInput,
    MessagingConversationPaginationInput,
} from '../@types/shared.types';
import { GET_MESSAGING_CONVERSATION_LIST } from '../graphql/resolvers/queries';

const CONVERSATION_PAGE_SIZE = 20;
const MESSAGES_POLL_INTERVAL = 5000;

interface ConversationsContextType {
    pollTick: number;
    conversations: MessagingConversation[];
    conversationsLoading: boolean;
    isInitialLoadComplete: boolean;
    conversationsFetchingMore: boolean;
    conversationsError: ErrorLike | undefined;
    conversationsHasNextPage: boolean;
    retryConversations: () => void;
    fetchMoreConversations: () => Promise<void>;
}

export const ConversationsContext = createContext<ConversationsContextType | null>(null);

const CONVERSATION_FILTERS: MessagingConversationFiltersInput = {};
const CONVERSATION_PAGINATION: MessagingConversationPaginationInput = {
    limit: CONVERSATION_PAGE_SIZE,
};

const ConversationsProvider = ({ children }: { children: ReactNode }) => {
    const [pollTick, setPollTick] = useState<number>(0);
    const [conversationsFetchingMore, setConversationsFetchingMore] = useState<boolean>(false);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState<boolean>(false);

    const fetchedCountRef = useRef(CONVERSATION_PAGE_SIZE);

    const { user, isClient, isProvider } = useAuth();

    const {
        data: conversationsData,
        previousData: previousConversationsData,
        loading: conversationsLoading,
        error: conversationsError,
        fetchMore: fetchMoreConversationQuery,
        refetch: refetchConversations,
    } = useQuery<
        { getMessagingConversationList: MessagingConversationConnection },
        {
            filters: MessagingConversationFiltersInput;
            pagination: MessagingConversationPaginationInput;
        }
    >(GET_MESSAGING_CONVERSATION_LIST, (!user || (!isClient && !isProvider)) ? skipToken : { variables: { filters: CONVERSATION_FILTERS, pagination: CONVERSATION_PAGINATION }, fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true, refetchWritePolicy: 'merge' });

    const resolvedData = conversationsData ?? previousConversationsData;

    const conversations = useMemo(
        () => resolvedData?.getMessagingConversationList?.items ?? [],
        [resolvedData]
    );

    const conversationPageInfo = useMemo(
        () => resolvedData?.getMessagingConversationList?.pageInfo,
        [resolvedData]
    );

    const conversationsHasNextPage = useMemo(
        () => conversationPageInfo?.hasNextPage ?? false,
        [conversationPageInfo]
    );

    const retryConversations = useCallback(() => {
        void refetchConversations({
            pagination: { limit: fetchedCountRef.current },
        });
    }, [refetchConversations]);

    const fetchMoreConversations = useCallback(async () => {
        if (
            !conversationsHasNextPage ||
            !conversationPageInfo?.endCursor ||
            conversationsLoading ||
            conversationsFetchingMore
        )
            return;

        setConversationsFetchingMore(true);

        await fetchMoreConversationQuery({
            variables: {
                filters: CONVERSATION_FILTERS,
                pagination: {
                    ...CONVERSATION_PAGINATION,
                    before: {
                        id: conversationPageInfo.endCursor.id,
                        timestamp: conversationPageInfo.endCursor.timestamp,
                    },
                },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;

                return {
                    getMessagingConversationList: {
                        ...fetchMoreResult.getMessagingConversationList,
                        items: [
                            ...prev.getMessagingConversationList.items,
                            ...fetchMoreResult.getMessagingConversationList.items,
                        ],
                    },
                };
            },
        });

        setConversationsFetchingMore(false);
    }, [
        conversationsHasNextPage,
        conversationPageInfo,
        conversationsLoading,
        conversationsFetchingMore,
        fetchMoreConversationQuery,
    ]);

    useEffect(() => {
        if (!isInitialLoadComplete && !conversationsLoading && conversationsData !== undefined) {
            setIsInitialLoadComplete(true);
        }
    }, [isInitialLoadComplete, conversationsLoading, conversationsData]);

    useEffect(() => {
        if (conversations.length > fetchedCountRef.current) {
            fetchedCountRef.current = conversations.length;
        }
    }, [conversations.length]);

    useEffect(() => {
        if (
            (isClient || isProvider) &&
            !conversationsLoading &&
            !conversationsFetchingMore &&
            !conversationsError
        ) {
            const id = setInterval(() => {
                void refetchConversations({
                    pagination: { limit: fetchedCountRef.current },
                });
                setPollTick((t) => t + 1);
            }, MESSAGES_POLL_INTERVAL);

            return () => clearInterval(id);
        }

        return;
    }, [
        isClient,
        isProvider,
        refetchConversations,
        conversationsLoading,
        conversationsFetchingMore,
        conversationsError,
    ]);

    return (
        <ConversationsContext.Provider
            value={{
                pollTick,
                conversations,
                conversationsLoading,
                isInitialLoadComplete,
                conversationsFetchingMore,
                conversationsError,
                conversationsHasNextPage,
                retryConversations,
                fetchMoreConversations,
            }}
        >
            {children}
        </ConversationsContext.Provider>
    );
};

export default ConversationsProvider;
